"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FieldWrapper } from "./context";
import { getDateString, getDateWorkedString } from "./date-helpers";
import type { FieldRenderContext } from "./context";

/**
 * Render a date field.
 */
export const DateFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a date"}
    >
      <Input
        id={fieldName}
        type="date"
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />
    </FieldWrapper>
  );
};

/**
 * Render a datetime field.
 */
export const DatetimeFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a date and time"}
    >
      <Input
        id={fieldName}
        type="datetime-local"
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />
    </FieldWrapper>
  );
};

/**
 * Render a time field.
 */
export const TimeFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a time"}
    >
      <Input
        id={fieldName}
        type="time"
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />
    </FieldWrapper>
  );
};

/**
 * Render a due-date field with quick-select buttons.
 */
export const DueDateFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentDueDate = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a due date"}
    >
      <Input
        id={fieldName}
        type="date"
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        placeholder={field.placeholder || "Select due date"}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />

      {/* Quick select buttons for due date */}
      <div className="flex flex-wrap gap-2 mt-2">
        <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Quick select:</span>
        {[
          { label: "1 day", days: 1 },
          { label: "3 days", days: 3 },
          { label: "7 days", days: 7 },
          { label: "10 days", days: 10 },
        ].map((option) => (
          <Button
            key={option.days}
            type="button"
            variant={currentDueDate === getDateString(option.days) ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setValue(fieldName, getDateString(option.days), {
                shouldValidate: true,
              })
            }
            className="text-xs h-7 px-2"
          >
            {option.label}
          </Button>
        ))}
      </div>
    </FieldWrapper>
  );
};

/**
 * Render a date-worked field with quick-select buttons.
 */
export const DateWorkedFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, register, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;
  const currentDateWorked = watchedValues[fieldName];

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || "Please select a date"}
    >
      <Input
        id={fieldName}
        type="date"
        {...register(fieldName)}
        className={
          hasError ? "border-red-500 border-2 focus:ring-red-500 focus:border-red-500" : ""
        }
        placeholder={field.placeholder || "Select date worked"}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
      />

      {/* Quick select buttons for date worked */}
      <div className="flex flex-wrap gap-2 mt-2">
        <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Quick select:</span>
        {[
          { label: "Today", days: 0 },
          { label: "Yesterday", days: 1 },
          { label: "2 days ago", days: 2 },
          { label: "3 days ago", days: 3 },
          { label: "4 days ago", days: 4 },
        ].map((option) => (
          <Button
            key={option.days}
            type="button"
            variant={currentDateWorked === getDateWorkedString(option.days) ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setValue(fieldName, getDateWorkedString(option.days), {
                shouldValidate: true,
              })
            }
            className="text-xs h-7 px-2"
          >
            {option.label}
          </Button>
        ))}
      </div>
    </FieldWrapper>
  );
};
